import {
  FinanceSummaryResponse,
  PlatformExpenseCategoryListResponse,
  PlatformExpenseCategoryView,
  PlatformExpenseListResponse,
  PlatformExpenseView,
  type CreatePlatformExpenseCategoryRequest,
  type CreatePlatformExpenseRequest,
  type UpdatePlatformExpenseCategoryRequest,
  type UpdatePlatformExpenseRequest,
} from '@odonix/contracts';
import { periodQuery, type Period } from '../../ui/period.js';
import { TenantsError } from './tenants.js';

/**
 * Деньги платформы: сводка `/v1/finance/summary`, расходы `/v1/expenses` и их
 * категории `/v1/expense-categories`.
 */

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

export interface ExpensesFilter {
  period: Period;
  categoryId?: string | undefined;
  q?: string | undefined;
  cursor?: string | undefined;
}

export interface FinanceApi {
  summary(period: Period): Promise<FinanceSummaryResponse>;
  expenses(filter: ExpensesFilter): Promise<PlatformExpenseListResponse>;
  createExpense(body: CreatePlatformExpenseRequest): Promise<PlatformExpenseView>;
  updateExpense(expenseId: string, body: UpdatePlatformExpenseRequest): Promise<PlatformExpenseView>;
  removeExpense(expenseId: string): Promise<void>;
  categories(): Promise<PlatformExpenseCategoryView[]>;
  createCategory(body: CreatePlatformExpenseCategoryRequest): Promise<PlatformExpenseCategoryView>;
  updateCategory(categoryId: string, body: UpdatePlatformExpenseCategoryRequest): Promise<PlatformExpenseCategoryView>;
}

async function toError(response: Response): Promise<TenantsError> {
  const body = (await response.json().catch(() => null)) as {
    code?: unknown;
    message?: unknown;
    fields?: Record<string, string>;
  } | null;
  return typeof body?.code === 'string' && typeof body.message === 'string'
    ? new TenantsError(body.code, body.message, body.fields)
    : new TenantsError('UNKNOWN', 'Сервер не ответил. Проверьте связь и повторите');
}

export function createFinanceApi(apiFetch: ApiFetch): FinanceApi {
  async function send(path: string, init?: RequestInit): Promise<unknown> {
    const response = await apiFetch(path, init);
    if (!response.ok) throw await toError(response);
    if (response.status === 204) return null;
    return response.json();
  }

  function json(method: string, body: unknown): RequestInit {
    return { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
  }

  const id = (value: string) => encodeURIComponent(value);

  return {
    async summary(period) {
      return FinanceSummaryResponse.parse(await send(`/v1/finance/summary?${periodQuery(period)}`));
    },

    async expenses(filter) {
      const params = new URLSearchParams({ from: filter.period.from, to: filter.period.to, limit: '100' });
      if (filter.categoryId !== undefined) params.set('categoryId', filter.categoryId);
      if (filter.q !== undefined && filter.q.trim() !== '') params.set('q', filter.q.trim());
      if (filter.cursor !== undefined) params.set('cursor', filter.cursor);
      return PlatformExpenseListResponse.parse(await send(`/v1/expenses?${params.toString()}`));
    },

    async createExpense(body) {
      return PlatformExpenseView.parse(await send('/v1/expenses', json('POST', body)));
    },

    async updateExpense(expenseId, body) {
      return PlatformExpenseView.parse(await send(`/v1/expenses/${id(expenseId)}`, json('PATCH', body)));
    },

    async removeExpense(expenseId) {
      await send(`/v1/expenses/${id(expenseId)}`, { method: 'DELETE' });
    },

    async categories() {
      return PlatformExpenseCategoryListResponse.parse(await send('/v1/expense-categories')).categories;
    },

    async createCategory(body) {
      return PlatformExpenseCategoryView.parse(await send('/v1/expense-categories', json('POST', body)));
    },

    async updateCategory(categoryId, body) {
      return PlatformExpenseCategoryView.parse(
        await send(`/v1/expense-categories/${id(categoryId)}`, json('PATCH', body)),
      );
    },
  };
}
